import { Fragment, useState, type ReactNode } from 'react';
import { Button, Dialog, Icon, cn, type IconName } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';

export type WelcomeChoice = 'tour' | 'skip' | 'close';

interface Card {
  readonly key: string;
  readonly title: MessageKey;
  readonly body: MessageKey;
  readonly visual: ReactNode;
}

/** The product, as the one sentence card two is about. */
const FLOW: readonly { icon: IconName; label: MessageKey }[] = [
  { icon: 'building', label: 'firstRun.flow.clients' },
  { icon: 'folder', label: 'firstRun.flow.projects' },
  { icon: 'list-checks', label: 'firstRun.flow.tasks' },
  { icon: 'clock', label: 'firstRun.flow.hours' },
  { icon: 'chart-column', label: 'firstRun.flow.reports' },
];

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-1 · the welcome cards, the first time a person signs in.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * What Forecastify is; how it fits together; the demo data, when there is some; and the tour offer
 * on the last card.
 *
 * ⚠️⚠️ **NOT IN THE MOCKUP, SO THE DESIGN SYSTEM DECIDES IT.** A centred `Dialog` — the one surface
 * the product already uses for a short, decisive flow — with no imagery (the readme's rule), an icon
 * tile at the icon set's empty-state size, and the flow as a row of the sidebar's own glyphs, so the
 * picture of the product is the product's own vocabulary.
 *
 * ⚠️ **Closing by any means counts as seen.** The X, Escape and the scrim all report `close`; a
 * welcome that came back after somebody dismissed it would be a nag, not a welcome.
 *
 * ⚠️ **Without `canTour` the last card offers no tour.** The tour arrives in a later chunk, and a
 * "Show me around" that showed nothing is the visible control that does nothing.
 */
export function WelcomeCards({
  open,
  demo,
  canTour,
  onDone,
}: {
  readonly open: boolean;
  /** Whether the workspace holds demo data — the third card is about it. */
  readonly demo: boolean;
  readonly canTour: boolean;
  readonly onDone: (choice: WelcomeChoice) => void;
}): ReactNode {
  const t = useT();
  const [index, setIndex] = useState(0);

  const cards: Card[] = [
    {
      key: 'what',
      title: 'firstRun.what.title',
      body: 'firstRun.what.body',
      visual: <Tile icon="sparkles" />,
    },
    {
      key: 'flow',
      title: 'firstRun.flow.title',
      body: 'firstRun.flow.body',
      visual: (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {FLOW.map((step, at) => (
            <Fragment key={step.label}>
              {at === 0 ? null : (
                <Icon name="chevron-right" size={13} className="text-muted-foreground" />
              )}
              <span className="bg-muted flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium">
                <Icon name={step.icon} size={13} className="text-primary" />
                {t(step.label)}
              </span>
            </Fragment>
          ))}
        </div>
      ),
    },
    ...(demo
      ? [
          {
            key: 'demo',
            title: 'firstRun.demo.title' as const,
            body: 'firstRun.demo.body' as const,
            visual: <Tile icon="database" />,
          },
        ]
      : []),
    {
      key: 'next',
      title: canTour ? 'firstRun.tour.title' : 'firstRun.start.title',
      body: canTour ? 'firstRun.tour.body' : 'firstRun.start.body',
      visual: <Tile icon={canTour ? 'play' : 'circle-check'} />,
    },
  ];

  /* The demo card can arrive while the cards are open — the seed finishes a few seconds after a
     sign-up — so the index is clamped rather than trusted. */
  const at = Math.min(index, cards.length - 1);
  const card = cards[at] as Card;
  const last = at === cards.length - 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone('close');
      }}
      title={t(card.title)}
      description={t(card.body)}
      width={480}
      closeLabel={t('common.close')}
      footer={
        <>
          {/* The dots are a picture of the position; the words are what a screen reader gets. */}
          <span className="sr-only">
            {t('firstRun.step', { current: at + 1, total: cards.length })}
          </span>
          <div className="mr-auto flex items-center gap-1.5" aria-hidden>
            {cards.map((candidate, dot) => (
              <span
                key={candidate.key}
                className={cn(
                  'size-1.5 rounded-full transition-colors duration-150',
                  dot === at ? 'bg-primary' : 'bg-border',
                )}
              />
            ))}
          </div>
          {at === 0 ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                setIndex(at - 1);
              }}
            >
              {t('firstRun.back')}
            </Button>
          )}
          {!last ? (
            <Button
              onClick={() => {
                setIndex(at + 1);
              }}
            >
              {t('firstRun.next')}
            </Button>
          ) : canTour ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onDone('skip');
                }}
              >
                {t('firstRun.tour.skip')}
              </Button>
              <Button
                onClick={() => {
                  onDone('tour');
                }}
              >
                {t('firstRun.tour.start')}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                onDone('close');
              }}
            >
              {t('firstRun.start.go')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex min-h-[72px] items-center justify-center py-2">{card.visual}</div>
    </Dialog>
  );
}

/** One icon at the empty-state size the design system's readme names (20px and up). */
function Tile({ icon }: { readonly icon: IconName }): ReactNode {
  return (
    <span className="bg-primary/10 text-primary grid size-12 place-items-center rounded-xl">
      <Icon name={icon} size={22} />
    </span>
  );
}
